import { TemplateElement, html, defineElement } from '@webtides/element-js';
import { classMap } from 'lit-html/directives/class-map.js';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock/lib/bodyScrollLock.es6';
import style from './modal-element.css';
import ModalElementEvent from './ModalElementEvent';
import '../../../../legacy-library/webcomponents/svg-icon/svg-icon';
import UserCentrics from '../../../../js/util/UserCentrics';

// implemented following https://micromodal.now.sh
export default class ModalElement extends TemplateElement {
    #identifier = null;
    #currentlyActiveModal = null;

    constructor() {
        super({ shadowRender: true, autoUpdate: false, styles: [style] });
        this._scrollPosition = 0;
    }

    connected() {
        this.#identifier = this.modalId || Math.random().toString(36).substr(2, 5);

        this.update();
        if (this.cloakUntilOpen === false) {
            this.setAttribute('cloak', 'false');
        }

        if (this.autoOpen && !this.previewMode && !sessionStorage.getItem(`modal-auto-open-${this.#identifier}`)) {
            setTimeout(() => {
                if (!UserCentrics.isConsentRequired()) {
                    this.open();
                    sessionStorage.setItem(`modal-auto-open-${this.#identifier}`, 'true');
                }
            }, this.autoOpenAfter * 1000);
        }
    }

    afterUpdate() {
        if (this.openState === true || window.location.hash.includes(this.#identifier)) this.open();
        this.setCssProperties();
    }
    disconnected() {
        this.removeEventListeners();
    }

    properties() {
        return {
            type: 'default', // ['default', 'full']

            modalId: false,

            minimalUi: false,
            disableFooter: false,
            disableClose: false,
            headerContainer: true,
            previewMode: false,
            closeText: '',
            updateHash: true,
            cloakUntilOpen: false,
            fitContent: false,
            openState: false,
            autoOpen: false,
            autoOpenAfter: 4,
            parameters: {},
        };
    }

    get isFull() {
        return this.type === 'full';
    }
    get isMinimal() {
        return this.minimalUi;
    }

    events() {
        return {
            '[ref=content]': {
                scroll: () => this.contentScrolled(),
            },
            window: {
                [ModalElementEvent.OPEN]: ev => {
                    if (ev.target !== this) {
                        this.#currentlyActiveModal = ev.target;
                    }
                },
                [ModalElementEvent.CLOSE]: ev => {
                    if (this.#currentlyActiveModal === ev.target) {
                        this.#currentlyActiveModal = null;
                    }
                },
                hashchange: () => {
                    if (this.updateHash) {
                        if (window.location.hash === `#${this.#identifier}` && this.openState === false) {
                            this.open();
                        } else if (!(window.location.hash === `#${this.#identifier}`) && this.openState === true) {
                            this.close();
                        }
                    }
                },
            },
        };
    }

    setCssProperties() {
        const gap = this.$refs.content.offsetWidth - this.$refs.content.clientWidth;
        this.style.setProperty('--scroll-bar-width', gap + 'px');
    }

    contentScrolled() {
        this.setCssProperties();
        const { scrollTop, scrollHeight, offsetHeight } = this.$refs.content;
        if (scrollTop > 0) {
            this.$refs.header.classList.add('has-scrolled-down');
        } else {
            this.$refs.header.classList.remove('has-scrolled-down');
        }
        if (this.$refs.footer && scrollTop + offsetHeight === scrollHeight) {
            this.$refs.footer.classList.remove('can-scroll-down');
        } else if (this.$refs.footer) {
            this.$refs.footer.classList.add('can-scroll-down');
        }
    }

    addEventListeners() {
        // this.getRoot().addEventListener('touchstart', this.onClick.bind(this));
        this.getRoot().addEventListener('click', this.onClick.bind(this));
        document.addEventListener('keydown', this.onKeyDown.bind(this));

        this._contentMutationObserver = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.type === 'childList') {
                    this.setCssProperties();
                }
            });
        });
        this._contentMutationObserver.observe(this, { childList: true, subtree: true });
    }

    removeEventListeners() {
        // this.getRoot().removeEventListener('touchstart', this.onClick);
        this.getRoot().removeEventListener('click', this.onClick);
        document.removeEventListener('keydown', this.onKeyDown);
        if (this._contentMutationObserver) this._contentMutationObserver.disconnect();
    }

    onClick(event) {
        if (event.target.hasAttribute('data-modal-close') && this.disableClose === false) {
            this.close();
            event.preventDefault();
        }
    }

    onKeyDown(event) {
        if (event.keyCode === 27 && this.disableClose === false) this.close();
    }

    open(parameters) {
        this.setAttribute('cloak', 'false');
        this.$refs.modal.setAttribute('aria-hidden', 'false');
        this.$refs.modal.classList.add('is-open');
        this.openState = true;
        this.parameters = parameters;
        this.dispatch(ModalElementEvent.OPEN, null, true);
        this._scrollPosition = document.scrollingElement.scrollTop;
        disableBodyScroll(this.$refs.content, {
            reserveScrollBarGap: true,
        });
        this.addEventListeners();

        if (this.$refs.footer && this.$refs.content.scrollHeight > this.$refs.content.offsetHeight) {
            this.$refs.footer.classList.add('can-scroll-down');
        } else if (this.$refs.footer) {
            this.$refs.footer.classList.remove('can-scroll-down');
        }

        if (this.updateHash) {
            window.location.hash = this.#identifier;
        }
        this.setCssProperties();
    }

    close() {
        this.$refs.modal.setAttribute('aria-hidden', 'true');
        this.$refs.modal.classList.remove('is-open');
        this.openState = false;
        this.dispatch(ModalElementEvent.CLOSE, null, true);
        this.removeEventListeners();
        enableBodyScroll(this.$refs.content);

        if (this.updateHash && !this.#currentlyActiveModal) {
            window.location.hash = '';
        }

        // restore scroll position
        document.scrollingElement.scrollTop = this._scrollPosition;
    }

    get currentlyActiveModal() {
        return this.#currentlyActiveModal;
    }

    template() {
        const modalClasses = {
            'is-full': this.type === 'full',
            'is-minimal': this.minimalUi,
            'is-preview': this.previewMode,
        };
        const modalHeaderClasses = {
            'p-4': this.type !== 'full',
            'md:p-8': this.type !== 'full',
            'xl:p-16': this.type !== 'full',
            'py-4': this.type === 'full',
            'md:py-8': this.type === 'full',
            'xl:pt-16': this.type === 'full',
            'xl:pb-6': this.type === 'full',
        };

        const modalDialogClasses = {
            'sm:w-auto': this.fitContent,
        };

        return html`
            <div id="modal-${this.#identifier}" ref="modal" class="modal ${classMap(modalClasses)}" aria-hidden="true">
                <div class="modal-overlay" tabindex="-1">
                    <div class="modal-backdrop" ?data-modal-close="${this.disableClose === false}"></div>
                    <div
                        ref="modalDialog"
                        class="modal-dialog w-full ${classMap(modalDialogClasses)}"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="modal-${this.#identifier}-title"
                    >
                        <header part="header" ref="header" class="border-b ${classMap(modalHeaderClasses)}">
                            <div class="${this.headerContainer ? 'container' : ''}">
                                <div class="flex items-center justify-between">
                                    <div class="flex-grow flex-shrink min-w-0">
                                        <slot name="title"></slot>
                                    </div>
                                    ${this.disableClose
                                        ? null
                                        : html`<div part="close" class="flex justify-end" data-modal-close>
                                              <slot name="close">
                                                  <span class="flex items-center hover:cursor-pointer">
                                                      <span class="mr-2 hidden md:inline">${this.closeText}</span>
                                                      <svg-icon name="close" class="w-4 h-4"></svg-icon>
                                                  </span>
                                              </slot>
                                          </div>`}
                                </div>
                            </div>
                        </header>
                        <main
                            ref="content"
                            part="content"
                            id="modal-${this.#identifier}-content"
                            class="h-full scrolling-touch overflow-y-auto overflow-x-hidden"
                        >
                            <slot name="content"></slot>
                        </main>
                        ${this.disableFooter === true
                            ? ''
                            : html`
                                  <footer ref="footer" class="p-4 md:p-8 xl:p-16 bg-white">
                                      <slot name="footer"></slot>
                                  </footer>
                              `}
                    </div>
                </div>
            </div>
        `;
    }
}

defineElement('modal-element', ModalElement);
